import React, { useEffect, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Controller, useForm } from 'react-hook-form';
import { Dialog, DialogProps, ElevatedButton, TextButton } from '@producer/ui-kit';

import { Project, Shot, Space } from '../../../app/entities';
// import { ImageRadioButton } from '../../../components/image-radio-button/image-radio-button';
import { PillsSelector } from '../../../components/pills-selector/pills-selector';
import { TextAreaInput } from '../../../components/textarea-input/TextAreaInput';
import { uiStore } from '../../../core/stores/ui-store';
import { observer } from 'mobx-react-lite';

import styles from './generate-ai-cover-image.modal.module.scss';
type GenerateShotCoverDto = {
  style: 'cinematic' | 'pencil' | 'cartoon';
  additionalPrompt?: string;
  promptPropsIds?: string[];
};

type GenerateAiCoverImageProps = DialogProps & {
  shot: Shot;
  space: Space;
};

const propKeysToExclude = ['order', 'cover'];
const preSelectedProps = [
  'description',
  'charactersIds',
  'locationId',
  'shotSize',
  'angle',
  'lens',
  'timing',
];

const styleOptions = [
  {
    label: 'Cinematic',
    value: 'cinematic',
    image: '/images/ai-image-styles/cinematic.jpg',
  },
  {
    label: 'Pencil',
    value: 'pencil',
    image: '/images/ai-image-styles/pencil.jpg',
  },
  {
    label: 'Cartoon 3D',
    value: 'cartoon',
    image: '/images/ai-image-styles/cartoon.jpg',
  },
];

export const GenerateAiCoverImage: React.FC<GenerateAiCoverImageProps> = observer(
  ({ onCancel, shot, space }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [propsOptions, setPropsOptions] = useState([]);

    const generateSchema = yup.object().shape({
      style: yup.mixed().oneOf(['cinematic', 'pencil', 'cartoon'], 'Invalid style').required(),
      additionalPrompt: yup.string().max(1000, 'Max 1000 characters').optional(),
      promptPropsIds: yup.array().of(yup.string()).optional(),
    });

    const { handleSubmit, control, setValue } = useForm<GenerateShotCoverDto>({
      mode: 'onSubmit',
      resolver: yupResolver(generateSchema),
      defaultValues: {
        style: 'cinematic',
        additionalPrompt: '',
        promptPropsIds: [],
      },
    });

    useEffect(() => {
      const project = Project.getOne(shot.projectId);

      if (project) {
        const options = [];
        const selectedProps = [];

        for (const prop of project.properties) {
          const isPreselected = preSelectedProps.includes(prop.key);
          if (isPreselected) {
            selectedProps.push(prop._id);
          }

          if (!propKeysToExclude.includes(prop.key)) {
            options.push({ label: prop.name, value: prop._id });
          }
        }

        setPropsOptions(options);
        if (selectedProps.length) {
          setValue('promptPropsIds', selectedProps);
        }
      }
    }, []);

    const onSubmit = async (input: GenerateShotCoverDto) => {
      if (isLoading) {
        return;
      }

      if (space.credits?.current < 5) {
        uiStore.openInsufficientPanel();
        return;
      }

      setIsLoading(true);

      for (const key in input) {
        if (input[key] === '') delete input[key];
      }

      space.decrementCredits(5);

      await shot.generateAiImage(input);

      onCancel();
      setIsLoading(false);
    };

    return (
      <Dialog
        title="Generate AI Image"
        onClose={onCancel}
        actions={[
          <TextButton key={'cancel'} onClick={onCancel} text={'Cancel'} />,
          <ElevatedButton
            key="generate"
            text="Generate"
            onClick={handleSubmit(onSubmit)}
            isLoading={isLoading}
          />,
        ]}
      >
        <div className={styles['content']}>
          {/**<StyleSelectionWrapper>
            <p className={styles['header']}>Style</p>

            <ImageRadioButton name="style" control={control} options={styleOptions} />
          </StyleSelectionWrapper>
           **/}

          <div>
            <p className={styles['header']}>Consider these properties for image generation</p>

            <PillsSelector
              required
              name="promptPropsIds"
              control={control}
              options={propsOptions}
            />
          </div>

          <Controller
            name="additionalPrompt"
            control={control}
            render={({ field: { onChange }, fieldState: { error } }) => (
              <TextAreaInput
                errorMessage={error?.message}
                placeholder="Additional AI Prompt: Specify the image generation"
                onChange={onChange}
              />
            )}
          />
        </div>
      </Dialog>
    );
  },
);

/** Exported, though nothing currently imports it. */
export const DateSelectorWrapper = ({
  className,
  ...props
}: React.ComponentPropsWithoutRef<'div'>) => (
  <div
    className={
      className
        ? `${styles['date-selector-wrapper']} ${className}`
        : styles['date-selector-wrapper']
    }
    {...props}
  />
);
